import { useState, useCallback } from 'react';
import { Navigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { subDays, startOfYear } from 'date-fns';
import { formatDistanceToNow } from 'date-fns';
import {
  ArrowLeft,
  BarChart3,
  Eye,
  Calendar,
  Clock,
  Users,
  TrendingUp,
  Zap,
  Target,
  UserPlus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Layout } from '@/components/layout/Layout';
import { useAuthStore } from '@/stores/authStore';
import { useQRCodes, QRCodeWithDynamic } from '@/hooks/useQRCodes';
import { useQRAnalytics } from '@/hooks/useQRAnalytics';
import { formatScanCount } from '@/lib/dynamic-qr-utils';
import { AnalyticsMetricCard } from '@/components/analytics/AnalyticsMetricCard';
import { TimeSeriesChart } from '@/components/analytics/TimeSeriesChart';
import { GeographicAnalysis } from '@/components/analytics/GeographicAnalysis';
import { DeviceBreakdown } from '@/components/analytics/DeviceBreakdown';
import { TimePatterns } from '@/components/analytics/TimePatterns';
import { ReferrerSources } from '@/components/analytics/ReferrerSources';
import { ScanActivityFeed } from '@/components/analytics/ScanActivityFeed';
import { ExportAnalytics } from '@/components/analytics/ExportAnalytics';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useQueryClient } from '@tanstack/react-query';

export default function Analytics() {
  const { user, isLoading: authLoading } = useAuthStore();
  const { qrCodes, isLoading: qrLoading } = useQRCodes();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  
  const selectedQRId = searchParams.get('qr');
  const [dateRange, setDateRange] = useState<string>('30d');
  
  // Filter to only dynamic QR codes
  const dynamicQRs = qrCodes.filter(qr => qr.isDynamic);
  
  // Selected QR code
  const selectedQR = dynamicQRs.find(qr => qr.id === selectedQRId) || dynamicQRs[0];
  
  // Calculate date range
  const getDateRange = useCallback(() => {
    const now = new Date();
    switch (dateRange) {
      case '7d':
        return { from: subDays(now, 7), to: now };
      case '30d':
        return { from: subDays(now, 30), to: now };
      case '90d':
        return { from: subDays(now, 90), to: now };
      case 'all':
        return { from: startOfYear(subDays(now, 365 * 2)), to: now };
      default:
        return { from: subDays(now, 30), to: now };
    }
  }, [dateRange]);
  
  const {
    analytics,
    timeSeries,
    geoData,
    deviceData,
    hourlyPatterns,
    bestTimes,
    referrerData,
    isLoading: analyticsLoading,
    scans,
  } = useQRAnalytics(selectedQR?.id || null, getDateRange());

  const handleQRChange = (qrId: string) => {
    setSearchParams({ qr: qrId });
  };

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['qr-scans'] });
  }, [queryClient]);

  if (authLoading || qrLoading) {
    return (
      <Layout>
        <div className="min-h-[80vh] flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </Layout>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (dynamicQRs.length === 0) {
    return (
      <Layout>
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 md:py-10">
          <Link to="/dashboard">
            <Button variant="ghost" className="mb-6">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
              <BarChart3 className="h-10 w-10 text-muted-foreground" />
            </div>
            <h2 className="text-2xl font-black tracking-tight mb-3">No Dynamic QR Codes Yet</h2>
            <p className="text-muted-foreground max-w-md mx-auto mb-6">
              Create a dynamic QR code to start tracking scans and viewing detailed analytics.
            </p>
            <Link to="/create">
              <Button className="gradient-hero">
                <Zap className="h-4 w-4 mr-2" />
                Create Dynamic QR Code
              </Button>
            </Link>
          </motion.div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 md:py-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8"
        >
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-black tracking-tight">Analytics</h1>
              <p className="text-sm text-muted-foreground">
                Track performance of your dynamic QR codes
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={selectedQR?.id || ''} onValueChange={handleQRChange}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select QR Code" />
              </SelectTrigger>
              <SelectContent>
                {dynamicQRs.map(qr => (
                  <SelectItem key={qr.id} value={qr.id}>
                    {qr.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {selectedQR && (
              <ExportAnalytics
                qrName={selectedQR.name}
                analytics={analytics}
                scans={scans}
              />
            )}
          </div>
        </motion.div>

        {analyticsLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <>
            {/* Metric Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 sm:gap-4 mb-8">
              <AnalyticsMetricCard
                title="Total Scans"
                value={formatScanCount(analytics.totalScans)}
                icon={Eye}
                trend={analytics.percentageChange}
                delay={0}
              />
              <AnalyticsMetricCard
                title="Unique IPs"
                value={analytics.uniqueIPs}
                icon={Users}
                delay={1}
              />
              <AnalyticsMetricCard
                title="Leads"
                value={analytics.leadCount}
                icon={UserPlus}
                delay={2}
              />
              <AnalyticsMetricCard
                title="Conv. Rate"
                value={`${analytics.conversionRate}%`}
                icon={Target}
                subtitle="Scans to leads"
                delay={3}
              />
              <AnalyticsMetricCard
                title="Scans Today"
                value={analytics.scansToday}
                icon={Calendar}
                delay={4}
              />
              <AnalyticsMetricCard
                title="This Week"
                value={analytics.scansThisWeek}
                icon={TrendingUp}
                delay={5}
              />
              <AnalyticsMetricCard
                title="This Month"
                value={analytics.scansThisMonth}
                icon={BarChart3}
                delay={6}
              />
              <AnalyticsMetricCard
                title="Avg per Day"
                value={analytics.avgScansPerDay}
                icon={Clock}
                delay={7}
              />
            </div>

            {/* Last scan info */}
            {analytics.lastScanAt && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-sm text-muted-foreground mb-6"
              >
                Last scan: {formatDistanceToNow(new Date(analytics.lastScanAt), { addSuffix: true })}
              </motion.div>
            )}

            {/* Charts Grid */}
            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              <TimeSeriesChart
                data={timeSeries}
                onRangeChange={setDateRange}
                currentRange={dateRange}
              />
              <GeographicAnalysis
                countries={geoData.countries}
                cities={geoData.cities}
              />
            </div>

            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              <DeviceBreakdown
                devices={deviceData.devices}
                operatingSystems={deviceData.operatingSystems}
                browsers={deviceData.browsers}
              />
              <TimePatterns
                hourlyPatterns={hourlyPatterns}
                bestTimes={bestTimes}
              />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <ReferrerSources data={referrerData} />
              <ScanActivityFeed
                scans={scans}
                qrCodeId={selectedQR?.id || null}
                onRefresh={handleRefresh}
              />
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
